import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Inbox,
  Sparkles,
  ScrollText,
  CalendarClock,
  MessagesSquare,
  ChevronRight,
  Activity,
  Database,
  Clock3,
  Users,
  Antenna,
  Brain,
  Cpu,
  MemoryStick,
  HardDrive,
  ListTodo,
} from "lucide-react";
import { DashboardApi, TasksApi, type TaskRow } from "../lib/api";
import { qk } from "../lib/query";
import { useStreamEvent } from "../lib/sse";
import { setPageHash } from "./registry";
import { Sparkline } from "../components/Sparkline";
import { PageBody, PageHeader } from "../components/PageScaffold";
import { CenteredSpinner, ErrorBanner, Pill } from "../components/primitives";
import { relativeTime, formatBytes } from "../lib/format";
import { cn } from "../lib/cn";
import type { MCActivityItem, MCNeedsItem, MCChannel, MCFleetAgent, MCUpcoming, MCMemory, MCHost } from "../lib/types";

export default function MissionControl() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: qk.dashboard.missionControl,
    queryFn: DashboardApi.missionControl,
    refetchInterval: 12_000,
  });

  // Live activity overlay — keep a small ring buffer of recent SSE events
  const [liveActivity, setLiveActivity] = useState<MCActivityItem[]>([]);
  useStreamEvent(["log", "request", "spawn", "bus", "config"], (ev) => {
    const item: MCActivityItem = (() => {
      switch (ev.type) {
        case "log":
          return { ts: ev.ts, type: ev.entry.level, text: ev.entry.msg, meta: ev.entry.fields };
        case "request":
          return { ts: ev.ts, type: "request", text: `new chat request — ${ev.platform}`, meta: { chat: ev.chat_jid } };
        case "spawn":
          return { ts: ev.ts, type: "spawn", text: `spawn ${ev.status} — ${ev.persona}`, meta: { id: ev.id } };
        case "bus":
          return { ts: ev.ts, type: "bus", text: `${ev.from} → ${ev.to}${ev.subject ? `: ${ev.subject}` : ""}` };
        case "config":
          return { ts: ev.ts, type: "config", text: `${ev.action} ${ev.object} ${ev.name}` };
        default:
          return { ts: ev.ts, type: "info", text: "" };
      }
    })();
    setLiveActivity((prev) => [item, ...prev].slice(0, 25));
    // Bump dashboard refresh after a moment to fold the change in
    qc.invalidateQueries({ queryKey: qk.dashboard.missionControl });
  });

  useEffect(() => {
    // Re-fetch on mount; SSE will keep things current after that
    refetch();
  }, [refetch]);

  const merged = useMemo(() => {
    if (!data) return liveActivity.slice(0, 30);
    const seen = new Set<string>();
    const out: MCActivityItem[] = [];
    for (const a of [...liveActivity, ...data.activity]) {
      const key = `${a.ts}:${a.text}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(a);
      if (out.length >= 30) break;
    }
    return out;
  }, [liveActivity, data]);

  if (isLoading && !data) return <CenteredSpinner label="loading mission control" />;
  if (error) return <PageBody><ErrorBanner error={error as Error} /></PageBody>;
  if (!data) return null;

  return (
    <>
      <PageHeader
        title="Mission Control"
        icon={LayoutDashboard}
        subtitle="What needs you, what's flowing, what's healthy."
      />
      <PageBody className="space-y-4">
        {/* Counters row */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          {data.counters.map((c) => (
            <CounterTile key={c.label} counter={c} />
          ))}
        </div>

        {/* Needs you + Activity feed */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2 panel overflow-hidden">
            <NeedsList items={data.needs} />
          </div>
          <div className="lg:col-span-3 panel overflow-hidden">
            <ActivityFeed items={merged} />
          </div>
        </div>

        {/* Agent tasks in progress */}
        <div className="panel overflow-hidden">
          <TasksPane />
        </div>

        {/* Channels health + Coming up */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-3 panel overflow-hidden">
            <ChannelsStrip channels={data.channels} />
          </div>
          <div className="lg:col-span-2 panel overflow-hidden">
            <UpcomingList items={data.upcoming} />
          </div>
        </div>

        {/* Fleet at a glance */}
        <div className="panel overflow-hidden">
          <FleetGlance fleet={data.fleet} memory={data.memory} />
        </div>

        {/* Host stats */}
        <div className="panel overflow-hidden">
          <HostStrip host={data.host} />
        </div>

        {/* Systems strip */}
        <div className="panel overflow-hidden">
          <SystemsStrip rows={data.systems} />
        </div>
      </PageBody>
    </>
  );
}

// ─── Agent tasks in progress ──────────────────────────────────────────────
function TasksPane() {
  const { data } = useQuery({
    queryKey: ["tasks", { agent: "" }],
    queryFn: () => TasksApi.list(),
    refetchInterval: 10_000,
  });
  const rows = (data?.tasks ?? []).filter((t) => ["active", "held", "blocked"].includes(t.status));
  const active = rows.filter((t) => t.status === "active");
  const held = rows.filter((t) => t.status === "held");
  const blocked = rows.filter((t) => t.status === "blocked");
  const order: TaskRow[] = [...active, ...blocked, ...held];
  return (
    <div>
      <button
        className="w-full px-4 py-3 border-b border-border flex items-center gap-2 hover:bg-surface-2 transition-colors"
        onClick={() => setPageHash("tasks")}
      >
        <ListTodo className="size-4 text-fg-muted" />
        <h2 className="text-sm font-semibold text-fg">Tasks in progress</h2>
        <span className="text-[11px] text-fg-subtle font-mono">
          {active.length} active · {blocked.length} blocked · {held.length} held
        </span>
        <ChevronRight className="size-4 text-fg-subtle ml-auto" />
      </button>
      {order.length === 0 ? (
        <div className="px-4 py-6 text-xs text-fg-subtle text-center">No active tasks — agents are idle or working ad-hoc.</div>
      ) : (
        <ul className="divide-y divide-border">
          {order.slice(0, 12).map((t) => (
            <li key={t.id} className="px-4 py-2 flex items-center gap-2">
              <span className={cn("size-1.5 rounded-full flex-shrink-0",
                t.status === "active" && "bg-accent animate-pulse-dot",
                t.status === "blocked" && "bg-danger",
                t.status === "held" && "bg-warning")} />
              <span className="text-sm text-fg truncate">{t.title}</span>
              {t.status === "blocked" && t.blocked_reason && (
                <span className="text-[11px] text-danger truncate max-w-[16rem]">— {t.blocked_reason}</span>
              )}
              <Pill tone="info">{t.owner_agent}</Pill>
              <span className="ml-auto text-[11px] text-fg-subtle font-mono whitespace-nowrap">{relativeTime(t.updated_at).replace(" ago", "")}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Tiles ──────────────────────────────────────────────────────────────

function CounterTile({ counter }: { counter: import("../lib/types").MCCounter }) {
  return (
    <div className="panel-elevated p-4 hover:border-border-strong transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium text-fg-muted uppercase tracking-wide">{counter.label}</div>
        {counter.delta && (
          <Pill tone={counter.delta.sign === "+" ? "success" : counter.delta.sign === "-" ? "danger" : "neutral"}>
            {counter.delta.sign}
            {counter.delta.value} · {counter.delta.window}
          </Pill>
        )}
      </div>
      <div className="mt-1 flex items-end justify-between gap-2">
        <div className="text-3xl font-semibold tabular-nums text-fg leading-none">{counter.value}</div>
        {counter.sparkline && counter.sparkline.some((v) => v > 0) ? (
          <div className="w-24 h-8 -mb-1 opacity-80">
            <Sparkline values={counter.sparkline} />
          </div>
        ) : null}
      </div>
      {counter.breakdown && counter.breakdown.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
          {counter.breakdown.map((b) => (
            <span key={b.label} className="text-fg-muted">
              <span className="font-mono text-fg tabular-nums">{b.value}</span>{" "}
              <span className="text-fg-subtle">{b.label}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Needs ──────────────────────────────────────────────────────────────

const NEEDS_ICON: Record<MCNeedsItem["kind"], React.ComponentType<{ className?: string }>> = {
  request: Inbox,
  unread: MessagesSquare,
  "spawn-failed": Sparkles,
  errors: ScrollText,
  "due-task": CalendarClock,
  merge: Users,
};

const NEEDS_TONE: Record<MCNeedsItem["kind"], string> = {
  request: "text-accent",
  unread: "text-info",
  "spawn-failed": "text-danger",
  errors: "text-warning",
  "due-task": "text-warning",
  merge: "text-info",
};

function NeedsList({ items }: { items: MCNeedsItem[] }) {
  return (
    <>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
        <h2 className="text-sm font-semibold text-fg">Needs you</h2>
        <span className="text-[11px] text-fg-muted">
          {items.length === 0 ? "all clear" : `${items.length} item${items.length === 1 ? "" : "s"}`}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="py-10 text-center">
          <div className="size-9 rounded-full mx-auto bg-success-subtle text-success flex items-center justify-center mb-2">
            ✓
          </div>
          <p className="text-sm text-fg">Nothing pending</p>
          <p className="text-xs text-fg-muted mt-0.5">No requests, no unreads, no recent failures.</p>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((n) => {
            const Icon = NEEDS_ICON[n.kind];
            const id = n.link.replace(/^#\/?/, "");
            return (
              <li key={n.kind}>
                <button
                  onClick={() => setPageHash(id as Parameters<typeof setPageHash>[0])}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-2 transition-colors group ring-focus"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className={cn("size-4 flex-shrink-0", NEEDS_TONE[n.kind])} />
                    <span className="text-sm text-fg">{n.label}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="font-mono text-sm tabular-nums text-fg">{n.count}</span>
                    <ChevronRight className="size-3.5 text-fg-subtle group-hover:text-fg transition-colors" />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

// ─── Activity ───────────────────────────────────────────────────────────

const TYPE_TONE: Record<string, string> = {
  info: "text-fg-muted",
  warn: "text-warning",
  error: "text-danger",
  request: "text-accent",
  spawn: "text-info",
  bus: "text-platform-agent-bus",
  config: "text-fg",
};

const TYPE_LABEL: Record<string, string> = {
  info: "log",
  warn: "warn",
  error: "error",
  request: "req",
  spawn: "spawn",
  bus: "bus",
  config: "cfg",
};

function ActivityFeed({ items }: { items: MCActivityItem[] }) {
  return (
    <>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
        <h2 className="text-sm font-semibold text-fg flex items-center gap-1.5">
          Activity
          <span className="text-[10px] text-success inline-flex items-center gap-1">
            <span className="size-1 rounded-full bg-success animate-pulse-dot" /> live
          </span>
        </h2>
        <span className="text-[11px] text-fg-muted">{items.length} recent</span>
      </div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-sm text-fg-muted">No recent activity yet</div>
      ) : (
        <ul className="divide-y divide-border max-h-[26rem] overflow-y-auto">
          {items.map((a, i) => (
            <li key={`${a.ts}-${i}`} className="flex items-start gap-3 px-4 py-2 hover:bg-surface-2/60">
              <span className="text-[10px] font-mono w-12 mt-0.5 flex-shrink-0 text-fg-subtle tabular-nums">
                {relativeTime(a.ts).replace(" ago", "")}
              </span>
              <span
                className={cn(
                  "text-[10px] uppercase tracking-wider font-mono font-semibold w-12 mt-0.5 flex-shrink-0",
                  TYPE_TONE[a.type] ?? "text-fg-muted",
                )}
              >
                {TYPE_LABEL[a.type] ?? a.type.slice(0, 4)}
              </span>
              <span className="text-sm text-fg flex-1 min-w-0 break-words">{a.text}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ─── Host stats ───────────────────────────────────────────────────────────

/** Bar/text colour by usage %: calm under 70, amber to 90, red above. */
function usageTone(pct: number): { bar: string; text: string } {
  if (pct >= 90) return { bar: "bg-danger", text: "text-danger" };
  if (pct >= 70) return { bar: "bg-warning", text: "text-warning" };
  return { bar: "bg-success", text: "text-success" };
}

function fmtUptime(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function HostStrip({ host }: { host: MCHost }) {
  const cards: Array<{ icon: typeof Cpu; label: string; pct: number; sub: string }> = [
    { icon: Cpu, label: "CPU", pct: host.cpu.percent, sub: `${host.cpu.cores} cores · load ${host.cpu.load1.toFixed(2)} / ${host.cpu.load5.toFixed(2)} / ${host.cpu.load15.toFixed(2)}` },
    { icon: MemoryStick, label: "Memory", pct: host.mem.percent, sub: `${formatBytes(host.mem.used_bytes)} / ${formatBytes(host.mem.total_bytes)}` },
    ...host.disks.map((d) => ({ icon: HardDrive, label: d.mount === "/" ? "Disk /" : "Disk", pct: d.percent, sub: `${formatBytes(d.used_bytes)} / ${formatBytes(d.total_bytes)} · ${d.mount}` })),
  ];
  return (
    <>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
        <h2 className="text-sm font-semibold text-fg">Host</h2>
        <span className="text-xs text-fg-muted font-mono truncate">{host.hostname}</span>
        <span className="text-xs text-fg-subtle ml-auto">up {fmtUptime(host.uptime_s)}</span>
      </div>
      <ul className="grid grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-border">
        {cards.map((card, i) => <MetricCard key={`${card.label}-${i}`} {...card} />)}
      </ul>
    </>
  );
}

function MetricCard({ icon: Icon, label, pct, sub }: { icon: typeof Cpu; label: string; pct: number; sub: string }) {
  const tone = usageTone(pct);
  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-2">
        <Icon className="size-3.5 text-fg-subtle flex-shrink-0" />
        <span className="text-xs font-medium text-fg">{label}</span>
        <span className={cn("text-sm font-semibold ml-auto tabular-nums", tone.text)}>{pct}%</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-surface-2 overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", tone.bar)} style={{ width: `${Math.min(100, Math.max(2, pct))}%` }} />
      </div>
      <div className="text-[11px] text-fg-muted mt-1.5 truncate" title={sub}>{sub}</div>
    </li>
  );
}

// ─── Systems ────────────────────────────────────────────────────────────

const SYSTEM_ICON = {
  bridge: Activity,
  bus: Database,
  scheduler: Clock3,
};

function SystemsStrip({ rows }: { rows: import("../lib/types").MCSystemRow[] }) {
  return (
    <>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
        <h2 className="text-sm font-semibold text-fg">Systems</h2>
      </div>
      <ul className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
        {rows.map((r) => {
          const Icon = SYSTEM_ICON[r.name];
          return (
            <li key={r.name} className="flex items-center gap-3 px-4 py-3">
              <div
                className={cn(
                  "size-7 rounded-md flex items-center justify-center flex-shrink-0",
                  r.ok ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger",
                )}
              >
                <Icon className="size-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-fg capitalize">{r.name}</span>
                  <span className={cn("size-1.5 rounded-full", r.ok ? "bg-success" : "bg-danger")} />
                </div>
                <div className="text-xs text-fg-muted mt-0.5 truncate">{r.detail}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

// ─── Channels health ──────────────────────────────────────────────────────

function ChannelsStrip({ channels }: { channels: MCChannel[] }) {
  return (
    <>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
        <Antenna className="size-3.5 text-fg-subtle" />
        <h2 className="text-sm font-semibold text-fg">Channels</h2>
        <span className="ml-auto text-[11px] text-fg-subtle">{channels.length}</span>
      </div>
      {channels.length === 0 ? (
        <div className="px-4 py-5 text-xs text-fg-subtle">No channels configured.</div>
      ) : (
        <ul className="divide-y divide-border">
          {channels.map((ch) => {
            const ok = ch.status === "connected";
            const tone = ok ? "bg-success" : ch.status === "error" ? "bg-danger" : "bg-fg-subtle";
            return (
              <li key={ch.name} className="flex items-center gap-2.5 px-4 py-2">
                <span className={cn("size-1.5 rounded-full flex-shrink-0", tone)} />
                <span className="text-sm font-mono text-fg truncate">{ch.name}</span>
                <span className="text-[10px] text-fg-subtle uppercase">{ch.platform}</span>
                <span className="ml-auto text-[11px] text-fg-subtle truncate max-w-[45%]">
                  {ch.last_error ? <span className="text-danger">{ch.last_error}</span> : ch.status}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

// ─── Coming up (scheduler) ─────────────────────────────────────────────────

function fromNow(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "now";
  const m = Math.round(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `in ${h}h`;
  return `in ${Math.round(h / 24)}d`;
}

function UpcomingList({ items }: { items: MCUpcoming[] }) {
  return (
    <>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
        <Clock3 className="size-3.5 text-fg-subtle" />
        <h2 className="text-sm font-semibold text-fg">Coming up</h2>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-5 text-xs text-fg-subtle">Nothing scheduled.</div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((u, i) => (
            <li key={i} className="flex items-center gap-2 px-4 py-2">
              <span className="text-sm text-fg truncate">{u.name}</span>
              <span className="text-[11px] text-fg-subtle">→ {u.target}</span>
              <span className="ml-auto text-[11px] font-mono text-accent whitespace-nowrap">{fromNow(u.fire_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ─── Fleet at a glance ──────────────────────────────────────────────────────

function FleetGlance({ fleet, memory }: { fleet: MCFleetAgent[]; memory: MCMemory }) {
  return (
    <>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
        <h2 className="text-sm font-semibold text-fg">Fleet at a glance</h2>
        <span className="ml-auto flex items-center gap-1.5 text-[11px] text-fg-subtle">
          <Brain className="size-3" />
          embeddings {memory.embeddings ? <span className="text-success">on</span> : <span className="text-warning">off</span>}
          {" · "}reflect <span className="font-mono text-fg-muted">{memory.reflectModel}</span>
        </span>
      </div>
      {fleet.length === 0 ? (
        <div className="px-4 py-5 text-xs text-fg-subtle">No agents.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-fg-subtle border-b border-border">
                <th className="text-left font-medium px-4 py-1.5">Agent</th>
                <th className="text-right font-medium px-3 py-1.5">Spawns 24h</th>
                <th className="text-right font-medium px-3 py-1.5">Sends 24h</th>
                <th className="text-right font-medium px-3 py-1.5">Memories</th>
                <th className="text-right font-medium px-4 py-1.5">Last active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {fleet.map((a) => (
                <tr key={a.agent} className={cn(!a.lastActive && "opacity-50")}>
                  <td className="px-4 py-1.5 font-medium text-fg">{a.agent}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-fg-muted">{a.spawns24h || "·"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-fg-muted">{a.sends24h || "·"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-fg-muted">{a.memories || "·"}</td>
                  <td className="px-4 py-1.5 text-right text-[11px] text-fg-subtle whitespace-nowrap">
                    {a.lastActive ? relativeTime(a.lastActive) : "idle"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
